import { MathFormula } from './Math';
export default class FuelCalcBase {

    #m_Exception;
    #m_Number;
    #m_Source;
    #m_Description;


    constructor() {
        this.#m_Exception = false;
        this.#m_Number = "";
        this.#m_Source = "";
        this.#m_Description = "";
    }

    //<summary>Returns a boolean value indicating whether an exception occurred.</summary>
    //<returns>true if an exception occurred, else false.</returns>
    //<remarks>ReadOnly.</remarks>
    get Exception() {
        return this.#m_Exception;
    }

    // <summary>Returns the exception number.</summary>
    // <returns>A string object containing the numeric code of the exception.</returns>
    // <remarks>ReadOnly.</remarks>
    get ExceptionNumber() {
       return this.#m_Number;

    }

    //<summary>Returns the source (property or method) where the exception occurred.</summary>
    //<returns>A string object containing the source name where the exception occurred.</returns>
    //<remarks>ReadOnly.</remarks>
    get ExceptionSource() {
        return this.#m_Source;
    }

    //<summary>Returns the description of the exception.</summary>
    //<returns>A string object containing the description of the exception.</returns>
    //<remarks>ReadOnly.</remarks>
    get ExceptionDescription() {
        return this.#m_Description;
    }

    //<summary>Assigns the System error details to the various exception properties.</summary>
    //<param name="source">Name of the property or method where the error occurred.</param>
    //<param name="description">Description of the error.</param>
    RaiseError_PropNme(source, description) {
        this.#m_Exception = true;
        this.#m_Number = "";
        this.#m_Source = `${this.toString()} - ${source}`;
        this.#m_Description = description;
    }


    //<summary>Assigns the FuelCalc error details to the various exception properties.</summary>
    //<param name="number">Number of the error that occurred.</param>
    //<param name="source">Name of the property or method where the error occurred.</param>
    RaiseError(number, source) {
        this.#m_Exception = true;
        this.#m_Number = number.toString();
        this.#m_Source = `${this.toString()} - ${source}`;
        this.#m_Description = this.GetFuelCalcErrorDescription(number);
    }

    // <summary>Returns the description of a particular component specific error.</summary>
    // <param name="number">The numeric code of the exception.</param>
    // <returns>A string object containing the description of the exception.</returns>
    GetFuelCalcErrorDescription(number) {
        let errorDs = "";

        switch (number) {
            case 1000: {
                errorDs = "Incorrect Unit Value.";
                break;
            }
            case 1001: {
                errorDs = `Invalid Numeric Entry! Please use the decimal symbol as per  ${"\n"} Regional Settings and don't use a digit grouping symbol.`;
                break;
            }
            case 1002: {
                errorDs = "Incorrect Calculation Method.";
                break;
            }
            case 1003: {
                errorDs = "Calculation Error.";
                break;
            }
            case 1004: {
                errorDs = "Insufficient Parameters.";
                break;
            }
            case 1005: {
                errorDs = "Incorrect Commodity.";
                break;
            }

            case 1020: {
                errorDs = "Density must be a numeric value between 611.5 and 1163 kg/m3.";
                break;
            }
            case 1021: {
                errorDs = "Gravity must be a positive numeric value.";
                break;
            }
            case 1022: {
                errorDs = "API must be a numeric value, between -10 and 100 Deg. API.";
                break;
            }
            case 1023: {
                errorDs = "Specific Gravity must be a numeric value between 0.6115 and 1.164";
                break;
            }

            case 1040: {
                errorDs = "Viscosity must be a numeric value, greater than 0.5 cSt.";
                break;
            }
            case 1045: {
                errorDs = "Kinematic Viscosity @50�C must be between 2 and 900 mm2/s.";
                break;
            }
            case 1046: {
                errorDs = "Viscosity in �Engler must be between 1.141 and 119.9 �Engler.";
                break;
            }
            case 1047: {
                errorDs = "Viscosity in Redwood No.1 must be between 32.1 and 9600 RW1.";
                break;
            }
            case 1048: {
                errorDs = "Viscosity in Saybolt Furol must be between 21 and 425 SSF @122�F.";
                break;
            }
            case 1049: {
                errorDs = "Viscosity in Saybolt Universal at 122�F must be between 32.6 and 4175.";
                break;
            }
            case 1050: {
                errorDs = "Viscosity in Saybolt Universal at 210�F must be between 29.5 and 290.";
                break;
            }
            case 1051: {
                errorDs = "Viscosity at 100�C must be between 2.0 and 1000 mm2/s.";
                break;
            }

            case 1060: {
                errorDs = "Temperature must be a positive numeric value.";
                break;
            }
            case 1061: {
                errorDs = "Temperature must be a numeric value.";
                break;
            }

            case 1080: {
                errorDs = "Sulphur must be a numeric value between 0 and 100.";
                break;
            }
            case 1081: {
                errorDs = "Ash must be a numeric value between 0 and 100.";
                break;
            }
            case 1082: {
                errorDs = "Water must be a numeric value between 0 and 100.";
                break;
            }
            case 1083: {
                errorDs = "Energy  must be a positive numeric value.";
                break;
            }
            case 1085: {
                errorDs = "Incorrect Fuel Type.";
                break;
            }
            case 1086: {
                errorDs = "Gross Heat of Combustion must be a positive numeric value.";
                break;
            }
            case 1087: {
                errorDs = "Hydrogen Content must be a numeric value between 0 and 100.";
                break;
            }

            case 1100: {
                errorDs = "FuelConstant_A must be a numeric value, between 7 and 12.";
                break;
            }
            case 1101: {
                errorDs = "FuelConstant_B must be a numeric value, between 2 and 5.";
                break;
            }

            case 1110: {
                errorDs = "Quantity  must be a positive numeric value.";
                break;
            }
            case 1111: {
                errorDs = "Volume  must be a positive numeric value.";
                break;
            }

            case 1150: {
                errorDs = "Component value must be greater than zero.";
                break;
            }
            case 1151: {
                errorDs = "Energy  must be positive numeric value.";
                break;
            }

        }
        return errorDs;

    }
    // <summary>Calls the VerifyDecimalCharacter method.</summary>
    // <returns>true if the decimal character used is the same as in regional settings; otherwise false.</returns>
    // <param name="value">Contains the property value to check.</param> 
    // <param name="source">Contains the property name, which will be passed into the exception handler.</param> 
    CheckDecimalCharacter(value, source) {
        //
        if (this.VerifyDecimalCharacter(value))
            return true;
        else {
            this.RaiseError(1001, source);
            return false;
        }
    }


    // <summary>Checks the decimal character used in a property value, against the decimal 
    // character in regional settings. if not the same, an exception will be raised.</summary>
    // <returns>true if the decimal character used is the same as in regional settings; otherwise false.</returns>
    // <param name="value">Contains the property value to check.</param> 
    VerifyDecimalCharacter(value) {
       // 
        let decimalSymbol = ".";
        // let number = parseFloat(value);
        let number = {};

        if (value.toString().trim().length > 0 && !MathFormula.TryParseFloat(value,number))
            return false

        if (decimalSymbol == "," && value.toString().indexOf(".") > 0) {
            //Check for presence of a dot if regional setting for decimal symbol is a comma.
            return false
        }
        else if (decimalSymbol == "." && value.toString().indexOf(",") > 0) {
            //Check for presence of a comma if regional setting for decimal symbol is a dot.
            return false
        }
        return true;
    }

}